<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  logo: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationStatus = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED')
);

const integrationStatusColor = computed(() =>
  props.enabled ? 'teal' : 'slate'
);

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);

const fallbackLogoSrc = computed(
  () => '/dashboard/images/integrations/webhook.png'
);

const lightLogoSrc = computed(() =>
  props.logo
    ? `/dashboard/images/integrations/${props.logo}`
    : `/dashboard/images/integrations/${props.id}.png`
);

const darkLogoSrc = computed(() =>
  props.logo
    ? lightLogoSrc.value
    : `/dashboard/images/integrations/${props.id}-dark.png`
);

const logoBackgroundClass = computed(() =>
  props.logo === 'salla.png' ? 'bg-white' : 'bg-n-alpha-3 dark:bg-n-alpha-2'
);

const onLogoError = event => {
  if (event.target.src.endsWith('/webhook.png')) return;
  event.target.src = fallbackLogoSrc.value;
};
</script>

<template>
  <div
    class="flex flex-col flex-1 min-w-0 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
  >
    <div class="flex items-start justify-between">
      <div class="flex h-12 w-12 mb-2">
        <img
          :src="lightLogoSrc"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden"
          :class="logoBackgroundClass"
          :alt="name"
          @error="onLogoError"
        />
        <img
          :src="darkLogoSrc"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block"
          :class="logoBackgroundClass"
          :alt="name"
          @error="onLogoError"
        />
      </div>
      <Label
        :label="integrationStatus"
        :color="integrationStatusColor"
        compact
      />
    </div>
    <div class="flex flex-col m-0 flex-1">
      <div
        class="font-medium mb-2 text-n-slate-12 flex flex-wrap gap-2 justify-between items-center"
      >
        <bdi
          class="flex-1 basis-32 min-w-0 break-words text-heading-3 text-n-slate-12"
        >
          {{ name }}
        </bdi>
        <router-link
          :to="actionURL"
          :aria-label="`${$t('INTEGRATION_APPS.CONFIGURE')}: ${name}`"
          class="inline-flex shrink-0 min-h-11 min-w-11 items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-n-blue-11 hover:underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-woot-500"
        >
          <Icon
            icon="i-woot-settings"
            class="size-4 shrink-0"
            aria-hidden="true"
          />
          {{ $t('INTEGRATION_APPS.CONFIGURE') }}
        </router-link>
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ replaceInstallationName(description) }}
      </p>
    </div>
  </div>
</template>
